import { FC, SVGProps } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useAtom } from 'jotai';

import IcDesktopViewFillBase from 'src/assets/images/icDesktopViewFill.svg';
import IcMobileDesktopViewFillBase from 'src/assets/images/icMobileDesktopViewFill.svg';
import IcMobileViewFillBase from 'src/assets/images/icSplitFill.svg';
import useSelectedTab from 'src/hooks/useSelectedTab';
import { creatorDesktopLayoutAtom, DesktopLayoutType } from 'src/stores/match/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';

const MatchViewLayoutIconCss = ({ theme }: { theme: Theme }) => css`
  color: ${theme.color.content9};
`;

const IcDesktopViewFill = styled(IcDesktopViewFillBase)`
  ${MatchViewLayoutIconCss}
`;

const IcMobileViewFill = styled(IcMobileViewFillBase)`
  ${MatchViewLayoutIconCss}
`;

const IcMobileDesktopViewFill = styled(IcMobileDesktopViewFillBase)`
  ${MatchViewLayoutIconCss}
`;

export const getCreatorViewModeEventName = (type: DesktopLayoutType) => {
  switch (type) {
    case 'WIDE':
      return 'long_form_view';
    case 'SHORTS':
      return 'short_form_view';
    case 'COMBINED':
      return 'split_view';
  }
};

export const CREATOR_VIEW_MODE_OPTIONS = [
  {
    type: 'WIDE' as const,
    titleTranslationKey: 'studio_view_desktop_title',
    subtextTranslationKey: 'studio_view_desktop_desc',
    shortTitleTranslationKey: 'view_mode_long_form_title',
    Icon: IcDesktopViewFill,
  },
  {
    type: 'SHORTS' as const,
    titleTranslationKey: 'studio_view_mobile_title',
    subtextTranslationKey: 'studio_view_mobile_desc',
    shortTitleTranslationKey: 'view_mode_short_form_title',
    Icon: IcMobileViewFill,
  },
  {
    type: 'COMBINED' as const,
    titleTranslationKey: 'studio_view_split_title',
    subtextTranslationKey: 'studio_view_split_desc',
    shortTitleTranslationKey: 'view_mode_dual_form_title',
    Icon: IcMobileDesktopViewFill,
  },
] as const satisfies {
  type: DesktopLayoutType;
  titleTranslationKey: string;
  subtextTranslationKey: string;
  shortTitleTranslationKey: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
}[];

type EventContext = {
  page: string;
  position: string;
};

const useCreatorViewMode = () => {
  const [currentViewMode, setCurrentViewMode] = useAtom(creatorDesktopLayoutAtom);
  const { selectedTab } = useSelectedTab();

  const changeViewMode = (type: DesktopLayoutType, eventContext: EventContext) => {
    pushEvent({
      eventName: EVENT_NAME.ANYWHERE__CLICK_CREATOR_VIEWMODE_BUTTON,
      eventType: EVENT_TYPE.CREATOR,
      eventParams: {
        ...eventContext,
        tab: selectedTab.name,
        target: 'creator_viewmode',
        action_category: 'click',
        from: getCreatorViewModeEventName(currentViewMode),
        to: getCreatorViewModeEventName(type),
      },
    });
    setCurrentViewMode(type);
  };

  return {
    currentViewMode,
    changeViewMode,
    viewModeOptions: CREATOR_VIEW_MODE_OPTIONS,
  };
};

export default useCreatorViewMode;
